import { EmptyPage } from "@components/common/empty-page/empty-page";
import { getSvgColoredIconCss, TABS_CONTENT_PADDING } from "@utils/ui-utils";
import { Grid2 } from "@mui/material";
import { ProjectMarkupsTable } from "@pages/project-details/project-markups/project-markups-table";
import { useProjectMarkups } from "@hooks/project-markups/use-project-markups";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { ProjectMarkupsTableHeaders } from "@pages/project-details/project-markups/project-markups-table-utils";
import { useMembers } from "@hooks/use-members";
import { useMemo } from "react";
import { ProjectMarkupsDetails } from "@pages/project-details/project-markups/project-markups-details";
import { MarkupProvider } from "@context-providers/markup/markup-context";
import { useAppSelector } from "@store/store-helper";
import { hasFetchedMarkupsIElementsSelector } from "@store/sdb-i-elements/sdb-i-elements-selectors";
import { selectIsProjectLoading } from "@store/i-elements/i-elements-selectors";
import { NoAnnotationsDuoIcon, red, XMarkCircleFillIcon } from "@flat-ui";

/**
 * Renders the project markups as a table
 */
export function ProjectMarkups({ projectId }: BaseProjectIdProps): JSX.Element {
  const hasFetchedMarkupsIElements = useAppSelector(
    hasFetchedMarkupsIElementsSelector
  );

  const { markups, isFetchingMarkups } = useProjectMarkups({ projectId });
  const isProjectLoading = useAppSelector(selectIsProjectLoading);
  const { isFetchingCompanyMembers, isFetchingProjectMembers } = useMembers();

  /** Flag that is set to true while all the data required to show the markups tab is not yet ready */
  const isLoading = useMemo(() => {
    return (
      isFetchingProjectMembers ||
      isFetchingMarkups ||
      isFetchingCompanyMembers ||
      isProjectLoading
    );
  }, [
    isFetchingProjectMembers,
    isFetchingMarkups,
    isFetchingCompanyMembers,
    isProjectLoading,
  ]);

  /** Content to be shown in the page, either empty/error page or markups table */
  const content = useMemo(() => {
    return markups.length === 0 && !isLoading ? (
      // If the markups are loaded and the project has no markups, show an empty page.
      // If the markups were not loaded because the request failed then show an error page.
      <EmptyPage
        title={hasFetchedMarkupsIElements ? "No annotations" : "Error"}
        subtitle={
          hasFetchedMarkupsIElements
            ? "There aren't annotations to show for this project."
            : "Failed to fetch the project annotations. Please reload the page and try again."
        }
        icon={
          hasFetchedMarkupsIElements
            ? NoAnnotationsDuoIcon
            : () => (
                <XMarkCircleFillIcon
                  sx={{
                    ...getSvgColoredIconCss(red[500]),
                    width: "61px",
                    height: "61px",
                  }}
                />
              )
        }
      />
    ) : (
      <MarkupProvider projectId={projectId} markups={markups}>
        <ProjectMarkupsDetails shouldShowSkeleton={isLoading} />
        <ProjectMarkupsTable
          isLoading={isLoading}
          requiredColumns={[
            ProjectMarkupsTableHeaders.name,
            ProjectMarkupsTableHeaders.assignee,
            ProjectMarkupsTableHeaders.dueDate,
            ProjectMarkupsTableHeaders.status,
            ProjectMarkupsTableHeaders.tags,
            ProjectMarkupsTableHeaders.attachments,
            ProjectMarkupsTableHeaders.options,
          ]}
          projectId={projectId}
        />
      </MarkupProvider>
    );
  }, [hasFetchedMarkupsIElements, isLoading, markups, projectId]);

  return (
    <Grid2 container sx={{ mt: TABS_CONTENT_PADDING, width: "100%" }}>
      <Grid2 width="100%">{content}</Grid2>
    </Grid2>
  );
}
